
import {
  Vue, Component, Prop, PropSync, Watch,
} from 'vue-property-decorator';
import { debounce } from 'ts-debounce';
import { AddressBookItem } from '@/features/shipment/types';
import AddressBookTable from '@/features/shipment/components/createShipment/AddressBookTable.vue';
import NoContentWithHint from '@/features/shipment/components/createShipment/NoContentWithHint.vue';
import EditAddressDialog from '@/features/shipment/components/createShipment/EditAddressDialog.vue';
import { showErrorAlertDialog, redirectTo } from '@/features/common/helpers';
import { getAddressesBook, getAddressesBookByCountry } from '@/features/shipment/services/addresses-book.services';
import eventBus from '@/features/common/eventBus';
import NoResultsFound from '@/features/common/components/NoResultsFound.vue';
import eventNames from '@/features/common/constants/events';

@Component({
  components: {
    AddressBookTable,
    NoContentWithHint,
    NoResultsFound,
    EditAddressDialog,
  },
})

export default class AddressBook extends Vue {
  @PropSync('show', { required: true }) syncedShow!: boolean;

  @Prop({ required: false, default: '' }) countryCode!: string;

  private isLoading = false;

  private searchField = '';

  private addressBookFiltered: AddressBookItem[] = [];

  private showEditDialog = false;

  private selectedAddressItem: AddressBookItem | null = null;

  private redirectTo = redirectTo;

  get addressBook(): Array<AddressBookItem> {
    return this.$store.getters.getAddressBook;
  }

  @Watch('addressBook.length', {
    immediate: true,
  })
  private updateFilteredAddressBook() {
    if (this.syncedShow && this.countryCode) return;
    this.addressBookFiltered = [...this.addressBook];
  }

  @Watch('syncedShow')
  private async onDialogOpen(isOpen: boolean) {
    if (!isOpen) return;

    this.searchField = '';

    if (!this.countryCode) {
      this.addressBookFiltered = [...this.addressBook];
      return;
    }

    this.isLoading = true;
    const items = await getAddressesBookByCountry({
      countryCode: this.countryCode,
    });
    this.addressBookFiltered = items;
    this.isLoading = false;
  }

  created(): void {
    eventBus.$on(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.clearSearchInput);
  }

  destroyed(): void {
    eventBus.$off(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.clearSearchInput);
  }

  private clearSearchInput() {
    this.searchField = '';
    this.debounceSearchHandler('');
  }

  private selectAddress(address: AddressBookItem) {
    this.$emit('select', address);
  }

  private searchAddressBookItem(value: string) {
    this.debounceSearch(value);
  }

  private debounceSearch = debounce(this.debounceSearchHandler, 500);

  private async debounceSearchHandler(value: string): Promise<void> {
    this.isLoading = true;

    const fetcher = this.countryCode ? getAddressesBookByCountry : getAddressesBook;
    const items = await fetcher({
      search: value,
      countryCode: this.countryCode || undefined,
    });
    if (items === null) {
      showErrorAlertDialog();
    } else {
      this.addressBookFiltered = items;
    }

    this.isLoading = false;
  }

  private appendAddressBookItems(items: AddressBookItem[]): void {
    if (!items.length) return;

    if (this.countryCode) {
      this.addressBookFiltered = [
        ...this.addressBookFiltered,
        ...items,
      ];
      return;
    }

    this.$store.commit('setAddressBook', [
      ...this.addressBook,
      ...items,
    ]);
  }

  private deleteItem(address: AddressBookItem): void {
    const storeIndex = this.addressBook.findIndex((item) => item.id === address.id);
    if (storeIndex !== -1) {
      this.$store.commit('deleteAddressBookItem', storeIndex);
    }

    this.addressBookFiltered = this.addressBookFiltered.filter((item) => item.id !== address.id);
  }

  private editAddress(address: AddressBookItem): void {
    this.selectedAddressItem = address;
    this.showEditDialog = true;
  }

  private handleAddressUpdated(updatedAddress: AddressBookItem): void {
    const storeIndex = this.addressBook.findIndex(
      (item) => item.id === updatedAddress.id,
    );
    if (storeIndex !== -1) {
      this.$store.commit('replaceAddressBookItem', {
        idx: storeIndex,
        value: updatedAddress,
      });
    }

    const filteredIndex = this.addressBookFiltered.findIndex(
      (item) => item.id === updatedAddress.id,
    );
    if (filteredIndex === -1) return;

    if (this.countryCode && updatedAddress.countryCode !== this.countryCode) {
      this.addressBookFiltered = this.addressBookFiltered.filter(
        (item) => item.id !== updatedAddress.id,
      );
      return;
    }

    this.$set(this.addressBookFiltered, filteredIndex, updatedAddress);
  }
}
